// /brokers/settlement/$brokerId — one broker's settlement position (SM-799
// drill-down, SM-621 phase 09a): the headline in words, every component
// behind it, the branch-by-branch drill-down of its live subtree, its
// payment requests (SM-888), and the out-of-band settlement notes in
// their own memo panel.
//
// The net position is information only (SM-878): it is never sent or
// asked for as one amount. A payment request asks for the external credit
// owed; commission is paid by payout batch; the two are never offset.
//
// In the currencies the node trades (SM-870): GOLD on a broker node, so
// the currency is shown rather than chosen and an old ?currency=RED link
// falls back to GOLD; all three on the SYSTEM_BROKER.
//
// Figures come from GET /admin/brokers/{id}/settlement-position — the same
// engine the broker's own portal reads, so the two show identical numbers
// for the same period. The writes on this page are recording or confirming
// a note (BROKER_SETTLEMENT_NOTE_RECORD / _CONFIRM) and recording or
// cancelling a payment request (BROKER_SETTLEMENT_NOTE_RECORD); none moves
// money anywhere, and a note's author may confirm it (PO, SM-914).
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { FixedCurrency } from "@/components/brokers/FixedCurrency";
import { EXPOSURE_CURRENCY, isBrokerCurrency } from "@/components/brokers/governance";
import {
  Magnitude,
  PaymentRequestsPanel,
  SettlementBreakdown,
  SettleAsOf,
  SettlementHeadline,
  SettlementNotesPanel,
} from "@/components/brokers/SettlementPanels";
import {
  SETTLEMENT_DEFAULT_DAYS,
  SETTLEMENT_WINDOWS,
  directionLabel,
  directionTone,
  useRenewingSettleWindow,
} from "@/components/brokers/settlement";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { useNodeCurrencies } from "@/components/brokers/useNodeCurrencies";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchSettlementPosition } from "@/lib/broker-queries";
import type { SettlementPositionFigures } from "@/lib/broker-types";
import { Perm } from "@/lib/perms";

interface Search {
  currency?: string;
  days?: number;
  scope?: "node" | "subtree";
}

export const Route = createFileRoute("/_app/brokers/settlement/$brokerId")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    currency: isBrokerCurrency(s.currency) ? s.currency : undefined,
    days: SETTLEMENT_WINDOWS.some((w) => w.days === Number(s.days)) ? Number(s.days) : undefined,
    scope: s.scope === "node" ? "node" : undefined,
  }),
  component: BrokerSettlementDetailPage,
});

const th = "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

function BrokerSettlementDetailPage() {
  const { brokerId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerSettlementView);
  const { currencies } = useNodeCurrencies(brokerId);
  const currency =
    search.currency && (currencies as readonly string[]).includes(search.currency) ? search.currency : EXPOSURE_CURRENCY;
  const days = search.days ?? SETTLEMENT_DEFAULT_DAYS;
  const scope = search.scope ?? "subtree";
  // Renewed after every write on the page, when the tab is shown again
  // over a minute later, every few minutes while it stays visible, and for
  // another node, currency, scope or period (SM-942, SM-958) — never left
  // at the time the page opened. The lists below are read again with it.
  const win = { currency, ...useRenewingSettleWindow(days, `${brokerId}:${currency}:${scope}`), scope };

  const q = useQuery({
    queryKey: brokerKeys.settlementPosition(brokerId, win),
    queryFn: () => fetchSettlementPosition(brokerId, win),
    // The figures stay on screen while another period, scope or window is
    // read — never another node's or currency's (SM-942): a branch click
    // or a currency switch shows the skeleton until its own read lands.
    placeholderData: (prev, prevQuery) => {
      const [, , id, f] = prevQuery?.queryKey ?? [];
      return id === brokerId && f?.currency === currency ? prev : undefined;
    },
    enabled: canView,
  });

  if (perms.loaded && !canView) {
    return (
      <EmptyState
        title="No access"
        description="You need the BROKER_SETTLEMENT_VIEW permission to see a broker's settlement position."
      />
    );
  }
  const p = q.data;
  // The node and its branches together, when the server sent them: no
  // single payment request asks for that total (SM-912), so the panels
  // label it as one.
  const consolidated = scope === "subtree" ? p?.consolidated : undefined;
  const headline = consolidated ?? p?.own;
  const set = (patch: Partial<Search>) => void navigate({ search: (s) => ({ ...s, ...patch }) });

  return (
    <div className="space-y-6">
      <div>
        <Link to="/brokers/settlement" search={{ days: search.days }} className="text-2xs text-neon hover:underline">
          All broker settlement positions
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">{p?.display_name || brokerId}</h1>
        <p className="font-mono text-2xs text-muted">{brokerId}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-32">
          {currencies.length > 1 ? (
            <Select
              label="Currency"
              value={currency}
              onChange={(e) => set({ currency: e.target.value === EXPOSURE_CURRENCY ? undefined : e.target.value })}
              options={currencies.map((c) => ({ value: c, label: c }))}
            />
          ) : (
            <FixedCurrency currency={currency} />
          )}
        </div>
        <div className="w-44">
          <Select
            label="Period"
            value={String(days)}
            onChange={(e) => set({ days: Number(e.target.value) === SETTLEMENT_DEFAULT_DAYS ? undefined : Number(e.target.value) })}
            options={SETTLEMENT_WINDOWS.map((w) => ({ value: String(w.days), label: w.label }))}
          />
        </div>
        <SegmentedControl<"node" | "subtree">
          label="Figures"
          value={scope}
          onChange={(v) => set({ scope: v === "subtree" ? undefined : v })}
          options={[
            { value: "subtree", label: "Node and its branches" },
            { value: "node", label: "Node alone" },
          ]}
        />
        <div className="ml-auto">
          <SettleAsOf at={q.dataUpdatedAt} fetching={q.isFetching} />
        </div>
        {currencies.length === 1 ? (
          <p className="basis-full text-2xs text-muted">Only GOLD is shown: this broker node trades GOLD only.</p>
        ) : null}
      </div>

      {q.isLoading || !perms.loaded ? <Skeleton className="h-32" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}

      {p && headline ? (
        <>
          <SettlementHeadline f={headline} currency={currency} subtree={!!consolidated} />
          <p className="text-2xs text-muted">{p.outstanding_caption}</p>
          <SettlementBreakdown
            f={headline}
            currency={currency}
            fees={p.fees}
            openReceivable={p.open_receivable}
            canLinkReceivable={perms.has(Perm.BrokerExposureView)}
            subtree={!!consolidated}
          />

          {scope === "subtree" && p.branches && p.branches.length > 0 ? (
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Branches</CardTitle>
                <span className="text-2xs text-muted">Each child with its own live subtree</span>
              </CardHeader>
              <CardBody className="p-0">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-2">
                    <tr>
                      <th className={`${th} px-5`}>Branch</th>
                      <th className={th}>Direction</th>
                      <th className={`${th} text-right`}>Net position (information only)</th>
                      <th className={`${th} text-right`}>Uncovered after collateral (information only)</th>
                      <th className={`${th} text-right`}>Open requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/60">
                      <td className={`${td} px-5`}>
                        <div className="font-medium">This node alone</div>
                      </td>
                      <td className={td}>
                        <Badge tone={directionTone(p.own.direction)} className="whitespace-nowrap">{directionLabel(p.own.direction)}</Badge>
                      </td>
                      <td className={`${td} text-right`}>
                        <Magnitude currency={currency} canon={p.own.outstanding} />
                      </td>
                      <td className={`${td} text-right`}>
                        {p.own.transfer_in_required !== "0" ? <Magnitude currency={currency} canon={p.own.transfer_in_required} /> : "—"}
                      </td>
                      <td className={`${td} text-right`}>
                        <OpenRequestsCell currency={currency} open={p.own.payment_requests_open} />
                      </td>
                    </tr>
                    {p.branches.map((b) => (
                      <tr
                        key={b.broker_id}
                        className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2/60"
                        onClick={() =>
                          void navigate({
                            to: "/brokers/settlement/$brokerId",
                            params: { brokerId: b.broker_id },
                            search: { currency: search.currency, days: search.days },
                          })
                        }
                      >
                        <td className={`${td} px-5`}>
                          <div className="font-medium">{b.display_name || b.broker_id}</div>
                          <div className="font-mono text-2xs text-muted">{b.broker_id}</div>
                        </td>
                        <td className={td}>
                          {b.figures.has_activity ? (
                            <Badge tone={directionTone(b.figures.direction)} className="whitespace-nowrap">{directionLabel(b.figures.direction)}</Badge>
                          ) : (
                            <span className="text-2xs text-muted">No activity yet</span>
                          )}
                        </td>
                        <td className={`${td} text-right`}>
                          <Magnitude currency={currency} canon={b.figures.outstanding} />
                        </td>
                        <td className={`${td} text-right`}>
                          {b.figures.transfer_in_required !== "0" ? (
                            <Magnitude currency={currency} canon={b.figures.transfer_in_required} />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={`${td} text-right`}>
                          <OpenRequestsCell currency={currency} open={b.figures.payment_requests_open} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          ) : null}

          {/* A new node or scope starts the list from its first page (SM-942). */}
          <PaymentRequestsPanel
            key={`${brokerId}:${scope}`}
            brokerId={brokerId}
            brokerName={p.display_name || brokerId}
            currency={currency}
            scope={scope}
            figures={p.own}
            canRecord={perms.has(Perm.BrokerSettlementNoteRecord)}
          />

          <SettlementNotesPanel
            brokerId={brokerId}
            currency={currency}
            scope={scope}
            caption={p.note_caption}
            canRecord={perms.has(Perm.BrokerSettlementNoteRecord)}
            canConfirm={perms.has(Perm.BrokerSettlementNoteConfirm)}
            branchNames={Object.fromEntries((p.branches ?? []).map((b) => [b.broker_id, b.display_name]))}
          />
        </>
      ) : null}
    </div>
  );
}

/** A branch's open payment requests: count, and what they still ask for. */
function OpenRequestsCell({
  currency,
  open,
}: {
  currency: string;
  open: SettlementPositionFigures["payment_requests_open"];
}) {
  if (!open) return <span className="text-muted">—</span>;
  if (open.count === 0) return <span className="font-mono text-muted">0</span>;
  return (
    <span className="inline-flex flex-col items-end">
      <span className="font-mono">{open.count}</span>
      <Magnitude currency={currency} canon={open.outstanding_amount} />
    </span>
  );
}
